'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare, Send, Server, Database, FolderOpen, Search, Command,
  History, Plus, ChevronLeft, ChevronRight, Wifi, WifiOff, Sun, Moon,
  CalendarDays, File, FileText, Image, FileAudio, FileArchive,
  HardDrive, Folder, ExternalLink, Download, X, RefreshCw, Loader2,
  Home, ChevronRight as ChevronRightIcon, Users,
} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';

// ───────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────

interface TelegramMsg {
  id: number;
  message_id: number;
  user: string;
  user_id: number;
  text: string;
  timestamp: number;
  color: string;
  emoji: string;
  time: string;
  senderName?: string;
  senderUsername?: string;
  createdAt: string;
  photo?: { file_id: string; width: number; height: number; file_size: number }[];
  document?: { file_name: string; file_size: number; mime_type: string; file_id: string }[];
  sticker?: { emoji: string; file_id: string; file_size: number; set_name: string };
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: { title: string; url: string }[];
}

interface NasFile {
  name: string;
  path: string;
  size: number;
  type: 'file' | 'dir';
  modified?: string;
}

// ───────────────────────────────────────────────
// Agent SENDER_MAP
// ───────────────────────────────────────────────

const SENDER_MAP: Record<string, { name: string; emoji: string; color: string; bg: string }> = {
  '경철 배': { name: '경철 배', emoji: '👤', color: '#7C3AED', bg: 'rgba(124,58,237,0.1)' },
  'desinsight_Benny_bot': { name: 'Benny', emoji: '🍏', color: '#10B981', bg: 'rgba(16,185,129,0.1)' },
  'snap_claude_bot': { name: 'Claude', emoji: '🤖', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
  'desinsight_Jessy_bot': { name: 'Jessi', emoji: '👩‍💻', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
  'desinsight_Dony_bot': { name: 'Dony', emoji: '🏠', color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
  'desinsight_Poby_bot': { name: 'Poby', emoji: '🐻', color: '#8B5CF6', bg: 'rgba(139,92,246,0.1)' },
  'desinsight_Shon_bot': { name: 'Shon', emoji: '🔧', color: '#06B6D4', bg: 'rgba(6,182,212,0.1)' },
  'desinsight_Tony_bot': { name: 'Tony', emoji: '🪟', color: '#EC4899', bg: 'rgba(236,72,153,0.1)' },
  'desinsight_Meety_bot': { name: 'Meety', emoji: '💻', color: '#14B8A6', bg: 'rgba(20,184,166,0.1)' },
};

const DEFAULT_AGENT = { name: '알 수 없음', emoji: '❓', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' };

function lookupAgent(userName: string | null): { name: string; emoji: string; color: string; bg: string } {
  if (!userName) return DEFAULT_AGENT;
  const exact = SENDER_MAP[userName];
  if (exact) return exact;
  for (const [key, val] of Object.entries(SENDER_MAP)) {
    if (userName.includes(key) || key.includes(userName)) return val;
  }
  return DEFAULT_AGENT;
}

// ───────────────────────────────────────────────
// inferSender — 경철 배 로직
// ───────────────────────────────────────────────

function inferSender(text: string, userField: string | null): { name: string; emoji: string; color: string; bg: string } {
  // 1. @ 호스트명 패턴
  const hostMatch = text.match(/@\s*(\S+)/);
  if (hostMatch) {
    const host = hostMatch[1].toLowerCase();
    if (host.includes('benny') || host === 'ben-macbookair-1') return SENDER_MAP['desinsight_Benny_bot'] || lookupAgent('Benny');
    if (host.includes('poby') || host === 'poby-macstudio') return SENDER_MAP['desinsight_Poby_bot'] || lookupAgent('Poby');
    if (host.includes('jessi') || host === 'gyungchul-macmini') return SENDER_MAP['desinsight_Jessy_bot'] || lookupAgent('Jessi');
    if (host.includes('dony') || host === 'imac-imac') return SENDER_MAP['desinsight_Dony_bot'] || lookupAgent('Dony');
    if (host.includes('shon') || host === 'cindy-1') return SENDER_MAP['desinsight_Shon_bot'] || lookupAgent('Shon');
    if (host.includes('tony') || host === 'cindydesk') return SENDER_MAP['desinsight_Tony_bot'] || lookupAgent('Tony');
    if (host.includes('meety') || host === 'insight-book') return SENDER_MAP['desinsight_Meety_bot'] || lookupAgent('Meety');
  }

  // 2. 이모지+이름+[= 패턴 (병기 표기)
  const emojiNameMatch = text.match(/^(\p{Emoji})\s*(\w+)(?:\[=\w+\])?\s*@/u);
  if (emojiNameMatch) {
    const name = emojiNameMatch[2].toLowerCase();
    for (const [, val] of Object.entries(SENDER_MAP)) {
      if (val.name.toLowerCase() === name) return val;
    }
  }

  // 3. userField 매핑
  if (userField) {
    const agent = lookupAgent(userField);
    if (agent.name !== '알 수 없음') return agent;
  }

  // 4. 짧은 메시지(<80자) → 경철 배
  if (text.length < 80) return SENDER_MAP['경철 배'];

  // 5. 긴 보고형(>500자, "대표님" 포함) → Poby
  if (text.length > 500 && text.includes('대표님')) return SENDER_MAP['desinsight_Poby_bot'] || lookupAgent('Poby');

  // 6. fallback → 경철 배
  return SENDER_MAP['경철 배'];
}

// ───────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────

const POBY_TUNNEL_URL = 'https://grid.snapcodex.com';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileIcon(fileName: string) {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext || '')) return <Image className="w-4 h-4" />;
  if (['pdf'].includes(ext || '')) return <FileText className="w-4 h-4" />;
  if (['doc', 'docx', 'hwp', 'txt', 'md'].includes(ext || '')) return <FileText className="w-4 h-4" />;
  if (['mp3', 'wav', 'ogg', 'flac'].includes(ext || '')) return <FileAudio className="w-4 h-4" />;
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '')) return <FileArchive className="w-4 h-4" />;
  return <File className="w-4 h-4" />;
}

function formatTime(isoStr: string) {
  const d = new Date(isoStr);
  return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(isoStr: string) {
  const d = new Date(isoStr);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return '오늘';
  return d.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
}

function renderMarkdown(text: string): React.ReactNode {
  let key = 0;
  const codeBlockRegex = /```(\w*)\n([\s\S]*?)```/g;
  const segments: { type: 'codeblock' | 'inline'; content: string; lang?: string }[] = [];
  let lastIdx = 0;
  let match;
  while ((match = codeBlockRegex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      segments.push({ type: 'inline', content: text.slice(lastIdx, match.index) });
    }
    segments.push({ type: 'codeblock', content: match[2], lang: match[1] || undefined });
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) {
    segments.push({ type: 'inline', content: text.slice(lastIdx) });
  }

  return segments.map((seg) => {
    if (seg.type === 'codeblock') {
      return (
        <pre key={++key} className="bg-gray-900/80 text-green-400 rounded-lg p-3 my-2 overflow-x-auto text-xs font-mono leading-relaxed">
          <code>{seg.content}</code>
        </pre>
      );
    }
    const inlineParts: React.ReactNode[] = [];
    const inlineRegex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
    let inlineLast = 0;
    while ((match = inlineRegex.exec(seg.content)) !== null) {
      if (match.index > inlineLast) {
        inlineParts.push(seg.content.slice(inlineLast, match.index));
      }
      if (match[2]) {
        inlineParts.push(<strong key={++key}>{match[2]}</strong>);
      } else if (match[4]) {
        inlineParts.push(<em key={++key}>{match[4]}</em>);
      } else if (match[6]) {
        inlineParts.push(<code key={++key} className="bg-gray-800/60 text-emerald-300 px-1.5 py-0.5 rounded text-xs font-mono">{match[6]}</code>);
      } else if (match[8] && match[9]) {
        inlineParts.push(<a key={++key} href={match[9]} target="_blank" rel="noopener noreferrer" className="text-emerald-400 underline hover:text-emerald-300">{match[8]}</a>);
      }
      inlineLast = match.index + match[0].length;
    }
    if (inlineLast < seg.content.length) {
      inlineParts.push(seg.content.slice(inlineLast));
    }
    return <span key={++key}>{inlineParts}</span>;
  });
}

// ───────────────────────────────────────────────
// NAS Tree Node
// ───────────────────────────────────────────────

interface TreeNode {
  name: string;
  path: string;
  type: 'dir' | 'file';
  size: number;
  modified?: string;
  children: TreeNode[];
  loaded?: boolean;
}

// ───────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────

export default function ChatAgentPage() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  // ── States ──
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const [showHistory, setShowHistory] = useState(false);
  const [showTelegram, setShowTelegram] = useState(false);
  const [showNasExplorer, setShowNasExplorer] = useState(false);
  const [tgMessages, setTgMessages] = useState<TelegramMsg[]>([]);
  const [tgLastId, setTgLastId] = useState<number>(0);
  const [tgConnected, setTgConnected] = useState(true);
  const tgPollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [sessions, setSessions] = useState<Array<{sessionId: string; channel: string; lastUpdated: string; messageCount: number}>>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // NAS explorer
  const [nasTree, setNasTree] = useState<TreeNode[]>([]);
  const [nasExpanded, setNasExpanded] = useState<Set<string>>(new Set());
  const [nasLoading, setNasLoading] = useState(false);
  const [nasPreviewFile, setNasPreviewFile] = useState<TreeNode | null>(null);
  const [nasPreviewContent, setNasPreviewContent] = useState<string>('');
  const [nasPreviewLoading, setNasPreviewLoading] = useState(false);
  const [nasOnline, setNasOnline] = useState<boolean | null>(null);

  // ── Scroll ──
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // ── Initial greeting ──
  useEffect(() => {
    setMessages([
      {
        id: '1',
        role: 'assistant',
        content: '안녕하세요! **SnapGRID Chat**입니다.\n\nNAS 기반 설계 아카이브 RAG 및 통합 에이전트 어시스턴트.\n무엇을 도와드릴까요?',
        timestamp: new Date(),
      },
    ]);
  }, []);

  // ── Session history ──
  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await fetch('/api/chat-history?action=sessions&limit=20');
      const data = await res.json();
      if (data.sessions) setSessions(data.sessions);
    } catch { /* ignore */ }
    setSessionsLoading(false);
  }, []);

  const loadSession = useCallback(async (sid: string) => {
    try {
      const res = await fetch('/api/chat-history?action=messages&sessionId=' + sid + '&limit=50');
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages.map((m: any, i: number) => ({
          id: 'hist-' + i,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: new Date(m.timestamp),
        })));
        setSessionId(sid);
        setShowHistory(false);
      }
    } catch { /* ignore */ }
  }, []);

  // ── Telegram message polling ──
  const fetchTgMessages = useCallback(async (initial = false) => {
    try {
      const since = initial ? 0 : tgLastId;
      let url = '/api/chat/proxy_messages?limit=50';
      if (since > 0) url += '&since=' + since;
      const res = await fetch(url);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      if (!data.messages) return;

      if (initial) {
        setTgMessages(data.messages);
        if (data.messages.length > 0) {
          setTgLastId(data.messages[data.messages.length - 1].timestamp);
        }
      } else if (data.messages.length > 0) {
        setTgMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const newMsgs = data.messages.filter((m: TelegramMsg) => !existingIds.has(m.id));
          if (newMsgs.length === 0) return prev;
          return [...prev, ...newMsgs].slice(-300);
        });
        setTgLastId(data.messages[data.messages.length - 1].timestamp);
      }
      setTgConnected(true);
    } catch {
      setTgConnected(false);
    }
  }, [tgLastId]);

  useEffect(() => {
    if (!showTelegram) return;
    fetchTgMessages(true);
    tgPollTimerRef.current = setInterval(() => fetchTgMessages(false), 5000);
    return () => {
      if (tgPollTimerRef.current) clearInterval(tgPollTimerRef.current);
    };
  }, [showTelegram, fetchTgMessages]);

  // ── NAS tree loading ──
  const loadNasTree = useCallback(async () => {
    setNasLoading(true);
    setNasOnline(null);
    try {
      const res = await fetch(POBY_TUNNEL_URL + '/api/webdav-ls?dir=', {
        headers: { 'Bypass-Tunnel-Reminder': 'true' },
      });
      const data = await res.json();
      const items: NasFile[] = data.items || data.entries || [];
      const tree: TreeNode[] = items
        .filter((i: NasFile) => i.type === 'dir')
        .sort((a: NasFile, b: NasFile) => a.name.localeCompare(b.name))
        .map((i: NasFile) => ({ name: i.name, path: i.name, type: 'dir' as const, size: i.size, children: [], loaded: false }));
      setNasTree(tree);
      setNasOnline(true);
    } catch {
      setNasOnline(false);
      setNasTree([
        { name: 'B_DESIGN', path: 'B_DESIGN', type: 'dir' as const, size: 0, children: [], loaded: false },
        { name: 'C_DOCUMENT', path: 'C_DOCUMENT', type: 'dir' as const, size: 0, children: [], loaded: false },
        { name: 'D_PROCUREMENT', path: 'D_PROCUREMENT', type: 'dir' as const, size: 0, children: [], loaded: false },
        { name: 'E_CONTRACT', path: 'E_CONTRACT', type: 'dir' as const, size: 0, children: [], loaded: false },
        { name: 'F_FINAL', path: 'F_FINAL', type: 'dir' as const, size: 0, children: [], loaded: false },
      ]);
    }
    setNasLoading(false);
  }, []);

  useEffect(() => {
    if (showNasExplorer && nasTree.length === 0 && !nasLoading) loadNasTree();
  }, [showNasExplorer, nasTree.length, nasLoading, loadNasTree]);

  const loadNasDir = async (dirPath: string): Promise<TreeNode[]> => {
    try {
      const encoded = encodeURIComponent(dirPath);
      const res = await fetch(POBY_TUNNEL_URL + '/api/webdav-ls?dir=' + encoded, {
        headers: { 'Bypass-Tunnel-Reminder': 'true' },
      });
      const data = await res.json();
      const items: NasFile[] = data.items || data.entries || [];
      return items
        .sort((a: NasFile, b: NasFile) => {
          if (a.type !== b.type) return a.type === 'dir' ? -1 : 1;
          return a.name.localeCompare(b.name);
        })
        .map((i: NasFile) => ({
          name: i.name,
          path: dirPath ? dirPath + '/' + i.name : i.name,
          type: i.type,
          size: i.size,
          modified: i.modified,
          children: [],
          loaded: false,
        }));
    } catch {
      return [];
    }
  };

  const toggleNasNode = async (nodePath: string, indexes: number[]) => {
    if (nasExpanded.has(nodePath)) {
      const next = new Set(nasExpanded);
      next.delete(nodePath);
      setNasExpanded(next);
      return;
    }

    const nextExpanded = new Set(nasExpanded);
    nextExpanded.add(nodePath);
    setNasExpanded(nextExpanded);

    const loadChildren = (nodes: TreeNode[], idxArr: number[]): Promise<TreeNode[]> => {
      if (idxArr.length === 0) return Promise.resolve(nodes);
      const [head, ...rest] = idxArr;
      if (rest.length === 0) {
        const node = nodes[head];
        if (!node.loaded && node.type === 'dir') {
          return loadNasDir(nodePath).then(children => {
            nodes[head] = { ...node, children, loaded: true };
            return nodes;
          });
        }
        return Promise.resolve(nodes);
      }
      return loadChildren(nodes[head].children, rest).then(updated => {
        nodes[head].children = updated;
        return nodes;
      });
    };

    const copy: TreeNode[] = JSON.parse(JSON.stringify(nasTree));
    loadChildren(copy, indexes).then(updated => setNasTree(updated));
  };

  const handleNasFileClick = async (file: TreeNode) => {
    setNasPreviewFile(file);
    setNasPreviewLoading(true);
    setNasPreviewContent('');
    try {
      const res = await fetch(POBY_TUNNEL_URL + '/api/webdav-preview?path=' + encodeURIComponent(file.path), {
        headers: { 'Bypass-Tunnel-Reminder': 'true' },
      });
      if (!res.ok) throw new Error('Preview not available');
      const data = await res.json();
      setNasPreviewContent(data.content || data.text || '(미리보기를 불러올 수 없습니다)');
    } catch {
      setNasPreviewContent('⚠️ 미리보기를 불러올 수 없습니다.\n파일이 너무 크거나 지원하지 않는 형식입니다.');
    }
    setNasPreviewLoading(false);
  };

  // ── Send ──
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    const assistantId = (Date.now() + 1).toString();
    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      sources: [],
    }]);

    try {
      const apiMessages = messages
        .filter(m => m.id !== assistantId)
        .map(m => ({ role: m.role, content: m.content }))
        .concat({ role: 'user', content: userMessage.content });

      const res = await fetch('/api/omni-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: apiMessages,
          metadata: { sessionId },
        }),
      });

      if (!res.ok) throw new Error('API Request Failed');

      const data = await res.json();
      if (data.sessionId && !sessionId) setSessionId(data.sessionId);

      let fullText = data.answer || data.content || '(응답 없음)';
      const sources: { title: string; url: string }[] = [];
      const sourceRegex = /\[출처:\s*([^\]]+)\]/g;
      let match;
      while ((match = sourceRegex.exec(fullText)) !== null) {
        if (!sources.some(s => s.title === match![1].trim())) {
          sources.push({ title: match[1].trim(), url: '#' });
        }
      }
      const cleanText = fullText.replace(/\[출처:\s*([^\]]+)\]/g, '').trim();

      setMessages(prev => prev.map(m => m.id === assistantId ? {
        ...m,
        content: cleanText,
        sources: sources.length > 0 ? sources : undefined,
      } : m));
    } catch {
      setMessages(prev => prev.map(m => m.id === assistantId ? {
        ...m,
        content: '⚠️ Poby Gateway (RAG Pipeline) 연결 서버 응답에 실패했습니다. 관리자에게 문의하세요.',
      } : m));
    } finally {
      setIsLoading(false);
    }
  };

  // ── Render NAS Tree ──
  const renderNasTreeNodes = (nodes: TreeNode[], depth = 0, parentIndexes: number[] = []): React.ReactNode => {
    return nodes.map((node, idx) => {
      const indexes = [...parentIndexes, idx];
      const key = node.path;
      const isExpanded = nasExpanded.has(key);
      const isDir = node.type === 'dir';
      const isSelected = nasPreviewFile?.path === key;

      return (
        <div key={key}>
          <button
            onClick={() => isDir ? toggleNasNode(key, indexes) : handleNasFileClick(node)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${
              isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
            } ${isSelected ? (isDark ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700') : ''}`}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
          >
            {isDir ? (
              <>
                <ChevronRightIcon className={`w-3 h-3 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''} ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <Folder className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
              </>
            ) : (
              <>
                <span className="w-3" />
                {getFileIcon(node.name)}
              </>
            )}
            <span className="text-xs truncate flex-1">{node.name}</span>
            {!isDir && node.size > 0 && (
              <span className={`text-[10px] flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                {formatFileSize(node.size)}
              </span>
            )}
          </button>
          {isDir && isExpanded && node.children && (
            <div>
              {node.children.length === 0 && node.loaded && (
                <div className={`text-[10px] text-center py-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}
                  style={{ paddingLeft: `${28 + depth * 16}px` }}>
                  (빈 폴더)
                </div>
              )}
              {renderNasTreeNodes(node.children, depth + 1, indexes)}
            </div>
          )}
        </div>
      );
    });
  };

  // ── Render Telegram attachment ──
  const renderTgAttachment = (msg: TelegramMsg) => {
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {msg.photo && msg.photo.length > 0 && (
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs ${
            isDark ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-600'
          }`}>
            <Image className="w-3.5 h-3.5" />
            <span>사진 {msg.photo.length}장</span>
            {msg.photo[0].file_size > 0 && (
              <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                {formatFileSize(msg.photo[0].file_size)}
              </span>
            )}
          </div>
        )}
        {msg.document && msg.document.length > 0 && msg.document.map((doc, i) => (
          <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs ${
            isDark ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-600'
          }`}>
            {getFileIcon(doc.file_name)}
            <span>{doc.file_name}</span>
            {doc.file_size > 0 && (
              <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                {formatFileSize(doc.file_size)}
              </span>
            )}
          </div>
        ))}
        {msg.sticker && (
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs ${
            isDark ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-600'
          }`}>
            <span className="text-lg">{msg.sticker.emoji || '🎨'}</span>
            <span>스티커 · {msg.sticker.set_name}</span>
          </div>
        )}
      </div>
    );
  };

  // Group Telegram messages by date
  const groupedTgMessages = tgMessages.reduce<{ date: string; msgs: TelegramMsg[] }[]>((acc, msg) => {
    const dateLabel = formatDate(msg.createdAt || msg.time);
    const last = acc[acc.length - 1];
    if (last && last.date === dateLabel) {
      last.msgs.push(msg);
    } else {
      acc.push({ date: dateLabel, msgs: [msg] });
    }
    return acc;
  }, []);

  // ───────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────

  return (
    <main className={'min-h-screen ' + (isDark ? 'bg-gray-950 text-gray-100' : 'bg-gray-50 text-gray-900') + ' font-sans selection:bg-emerald-500/30'}>

      {/* ── Top Navigation Bar ── */}
      <header className={'fixed top-0 left-0 right-0 z-50 h-14 border-b backdrop-blur-md ' + (isDark ? 'bg-gray-950/80 border-white/10' : 'bg-white/80 border-gray-200')}>
        <div className="max-w-full h-full mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={'p-1.5 rounded-lg ' + (isDark ? 'bg-emerald-500/20' : 'bg-emerald-100')}>
              <MessageSquare className={'w-4 h-4 ' + (isDark ? 'text-emerald-400' : 'text-emerald-600')} />
            </div>
            <span className={'text-sm font-bold tracking-tight ' + (isDark ? 'text-white' : 'text-gray-900')}>
              SnapGRID Chat
            </span>
            <div className={'hidden sm:flex items-center gap-1 ml-2 text-[10px] font-medium px-2 py-0.5 rounded-full ' + (isDark ? 'bg-gray-800 text-emerald-400' : 'bg-emerald-50 text-emerald-600')}>
              <Server className="w-3 h-3" />
              NAS RAG Ready
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowNasExplorer(!showNasExplorer)}
              className={'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ' + (
                showNasExplorer
                  ? (isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700')
                  : (isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700')
              )}
            >
              <HardDrive className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">NAS</span>
            </button>
            <a href="/chat/group"
              className={'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ' + (isDark ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300' : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700')}
            >
              <Users className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">그룹챗</span>
            </a>
            <button
              onClick={() => window.open('/scheduler', '_blank')}
              className={'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ' + (isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700')}
            >
              <CalendarDays className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">스케줄러</span>
            </button>
            <button
              onClick={toggleTheme}
              className={'p-2 rounded-lg transition-colors ' + (isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500')}
              title={isDark ? '라이트 모드' : '다크 모드'}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </header>

      {/* ── Main Layout ── */}
      <div className="pt-14 max-w-full mx-auto h-screen flex overflow-hidden">

        {/* ── SIDEBAR LEFT: Session History ── */}
        <AnimatePresence>
          {showHistory && (
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className={'flex-shrink-0 overflow-hidden border-r ' + (isDark ? 'bg-gray-900 border-white/10' : 'bg-white border-gray-200')}
            >
              <div className="p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <History className={'w-4 h-4 ' + (isDark ? 'text-gray-400' : 'text-gray-500')} />
                    <span className="text-sm font-semibold">세션 기록</span>
                  </div>
                  <button onClick={() => setShowHistory(false)}
                    className={'p-1 rounded-md ' + (isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500')}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                {sessionsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className={'w-5 h-5 animate-spin ' + (isDark ? 'text-emerald-400' : 'text-emerald-600')} />
                  </div>
                ) : sessions.length === 0 ? (
                  <p className={'text-xs text-center py-8 ' + (isDark ? 'text-gray-600' : 'text-gray-400')}>
                    저장된 세션이 없습니다
                  </p>
                ) : (
                  <div className="space-y-1">
                    {sessions.map(s => (
                      <button key={s.sessionId} onClick={() => loadSession(s.sessionId)}
                        className={'w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ' + (isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-700')}>
                        <div className="font-medium truncate">{s.channel || s.sessionId.slice(0, 16) + '...'}</div>
                        <div className={'text-[10px] mt-0.5 ' + (isDark ? 'text-gray-600' : 'text-gray-400')}>
                          {s.messageCount}개 메시지 · {new Date(s.lastUpdated).toLocaleDateString('ko-KR')}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* ── NAS EXPLORER (Left Panel) ── */}
        <AnimatePresence>
          {showNasExplorer && (
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: '40%', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className={'flex-shrink-0 overflow-hidden border-r flex flex-col ' + (isDark ? 'bg-gray-900/50 border-white/10' : 'bg-gray-50 border-gray-200')}
            >
              {/* NAS header */}
              <div className={'px-3 py-2.5 border-b flex items-center justify-between ' + (isDark ? 'border-white/10' : 'border-gray-200')}>
                <div className="flex items-center gap-2">
                  {nasOnline === true ? <Wifi className="w-3.5 h-3.5 text-emerald-400" /> :
                    nasOnline === false ? <WifiOff className="w-3.5 h-3.5 text-red-400" /> :
                    null}
                  <span className="text-xs font-semibold">NAS 탐색기</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={loadNasTree}
                    className={'p-1 rounded-md ' + (isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-500')}>
                    <RefreshCw className={'w-3.5 h-3.5 ' + (nasLoading ? 'animate-spin' : '')} />
                  </button>
                  <button onClick={() => setShowNasExplorer(false)}
                    className={'p-1 rounded-md ' + (isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-500')}>
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* NAS tree + preview split layout */}
              <div className="flex flex-1 overflow-hidden">
                {/* Tree panel */}
                <div className={'flex-1 overflow-y-auto p-2 ' + (nasPreviewFile ? 'border-r w-1/2 ' : 'w-full ') + (isDark ? 'border-white/10' : 'border-gray-200')}>
                  {nasLoading ? (
                    <div className="flex justify-center items-center h-full">
                      <Loader2 className={'w-5 h-5 animate-spin ' + (isDark ? 'text-emerald-400' : 'text-emerald-600')} />
                    </div>
                  ) : nasTree.length === 0 ? (
                    <div className={'text-xs text-center py-8 ' + (isDark ? 'text-gray-600' : 'text-gray-400')}>
                      NAS에 접근할 수 없습니다
                    </div>
                  ) : renderNasTreeNodes(nasTree)}
                </div>

                {/* Preview panel */}
                {nasPreviewFile && (
                  <div className={'w-1/2 overflow-y-auto p-3 ' + (isDark ? 'bg-gray-950/50' : 'bg-white')}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {getFileIcon(nasPreviewFile.name)}
                        <span className="text-xs font-medium truncate">{nasPreviewFile.name}</span>
                      </div>
                      <button onClick={() => setNasPreviewFile(null)}
                        className={'p-0.5 rounded flex-shrink-0 ' + (isDark ? 'hover:bg-white/10 text-gray-500' : 'hover:bg-gray-100 text-gray-400')}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    {nasPreviewFile.size > 0 && (
                      <p className={'text-[10px] mb-2 ' + (isDark ? 'text-gray-600' : 'text-gray-400')}>
                        {formatFileSize(nasPreviewFile.size)}
                        {nasPreviewFile.modified ? ' · ' + new Date(nasPreviewFile.modified).toLocaleString('ko-KR') : ''}
                      </p>
                    )}
                    <div className="flex gap-1 mb-3">
                      <a href={POBY_TUNNEL_URL + '/api/webdav-preview?path=' + encodeURIComponent(nasPreviewFile.path)}
                        target="_blank" rel="noopener noreferrer"
                        className={'flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium ' + (isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600')}>
                        <ExternalLink className="w-3 h-3" />
                        열기
                      </a>
                    </div>
                    {nasPreviewLoading ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className={'w-4 h-4 animate-spin ' + (isDark ? 'text-emerald-400' : 'text-emerald-600')} />
                      </div>
                    ) : (
                      <pre className={'text-xs whitespace-pre-wrap break-words leading-relaxed ' + (isDark ? 'text-gray-300' : 'text-gray-700')}
                        style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                        {nasPreviewContent}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* ── CHAT PANEL (Center) ── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Chat header */}
          <div className={'flex items-center justify-between px-4 py-2 border-b ' + (isDark ? 'bg-gray-900/50 border-white/10' : 'bg-white border-gray-200')}>
            <div className="flex items-center gap-2">
              <button onClick={() => { setShowHistory(true); loadSessions(); }}
                className={'p-1.5 rounded-lg transition-colors ' + (isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500')}>
                <History className="w-4 h-4" />
              </button>
              <button onClick={() => setShowTelegram(!showTelegram)}
                className={'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ' + (
                  showTelegram
                    ? (isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700')
                    : (isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500')
                )}>
                <MessageSquare className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Telegram</span>
                <span className={'w-1.5 h-1.5 rounded-full ml-0.5 ' + (tgConnected ? 'bg-emerald-400' : 'bg-red-400')} />
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div className={'flex-1 overflow-y-auto px-4 py-3 space-y-3 ' + (isDark ? 'bg-gray-950/30' : 'bg-gray-50/80')}>
            {messages.map((msg) => {
              const agentInfo = msg.role === 'assistant'
                ? inferSender(msg.content, null)
                : { name: '나', emoji: '👤', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' };
              const color = agentInfo.color;
              const bg = agentInfo.bg;

              return (
                <div
                  key={msg.id}
                  className={'group relative flex gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 hover:shadow-sm border-l-[3px]'}
                  style={{
                    borderLeftColor: color,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                    marginBottom: '12px',
                  }}
                >
                  {/* Profile circle */}
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-base flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: bg }}
                  >
                    {agentInfo.emoji}
                  </div>

                  {/* Message content */}
                  <div className="flex-1 min-w-0">
                    {/* Sender name + time */}
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="text-xs font-bold tracking-tight"
                        style={{ color: color }}
                      >
                        {agentInfo.name}
                      </span>
                      <span className={'text-[10px] ' + (isDark ? 'text-gray-600' : 'text-gray-400')}>
                        {formatTime(msg.timestamp.toISOString())}
                      </span>
                      {msg.role === 'assistant' && (
                        <span className={'text-[10px] px-1.5 py-0.5 rounded-full ' + (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600')}>
                          AI
                        </span>
                      )}
                    </div>

                    {/* Message text */}
                    <div className={'text-sm leading-relaxed ' + (isDark ? 'text-gray-200' : 'text-gray-800')}>
                      {renderMarkdown(msg.content)}
                    </div>

                    {/* Sources */}
                    {msg.sources && msg.sources.length > 0 && (
                      <div className={'mt-2 pt-2 border-t text-xs ' + (isDark ? 'border-white/5 text-gray-500' : 'border-gray-100 text-gray-400')}>
                        <span className="font-medium">출처: </span>
                        {msg.sources.map((s, i) => (
                          <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                            className={'hover:underline mx-1 ' + (isDark ? 'text-emerald-400' : 'text-emerald-600')}>
                            {s.title}{i < msg.sources!.length - 1 ? ', ' : ''}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className={'border-t px-4 py-3 ' + (isDark ? 'bg-gray-900/80 border-white/10' : 'bg-white border-gray-200')}>
            <div className={'flex items-center gap-2 rounded-xl border px-3 py-2 ' +
              (isDark ? 'bg-gray-800 border-white/10 focus-within:border-emerald-500/50' : 'bg-gray-50 border-gray-200 focus-within:border-emerald-400')}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="메시지를 입력하세요..."
                className={'flex-1 bg-transparent text-sm outline-none placeholder:text-xs ' +
                  (isDark ? 'text-gray-200 placeholder-gray-500' : 'text-gray-700 placeholder-gray-400')}
                disabled={isLoading}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className={'p-1.5 rounded-lg transition-colors ' + (
                  !input.trim() || isLoading
                    ? (isDark ? 'text-gray-600' : 'text-gray-300')
                    : (isDark ? 'text-emerald-400 hover:bg-emerald-500/20' : 'text-emerald-600 hover:bg-emerald-50')
                )}
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── TELEGRAM PANEL (Right) ── */}
        <AnimatePresence>
          {showTelegram && (
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 360, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className={'flex-shrink-0 overflow-hidden border-l ' + (isDark ? 'bg-gray-900 border-white/10' : 'bg-white border-gray-200')}
            >
              <div className={'px-3 py-2.5 border-b flex items-center justify-between ' + (isDark ? 'border-white/10' : 'border-gray-200')}>
                <div className="flex items-center gap-2">
                  <MessageSquare className={'w-4 h-4 ' + (isDark ? 'text-emerald-400' : 'text-emerald-600')} />
                  <span className="text-xs font-semibold">SnapGRID 톡방</span>
                  <span className={'w-1.5 h-1.5 rounded-full ' + (tgConnected ? 'bg-emerald-400' : 'bg-red-400')} />
                </div>
                <button onClick={() => setShowTelegram(false)}
                  className={'p-1 rounded-md ' + (isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500')}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="overflow-y-auto h-[calc(100vh-100px)]">
                {groupedTgMessages.length === 0 ? (
                  <div className={'text-xs text-center py-8 ' + (isDark ? 'text-gray-600' : 'text-gray-400')}>
                    {tgConnected ? '메시지 없음' : '연결 끊김'}
                  </div>
                ) : groupedTgMessages.map((group) => (
                  <div key={group.date}>
                    <div className={'flex justify-center py-2'}>
                      <span className={'text-[10px] px-2 py-0.5 rounded-full ' + (isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500')}>
                        {group.date}
                      </span>
                    </div>
                    {group.msgs.map((msg) => {
                      const agent = inferSender(msg.text || '', msg.user || msg.senderName || null);
                      return (
                        <div key={msg.id} className={'px-3 py-2.5 border-b ' + (isDark ? 'border-white/5 hover:bg-white/[0.02]' : 'border-gray-100 hover:bg-gray-50')}>
                          <div className="flex items-start gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs flex-shrink-0"
                              style={{ backgroundColor: agent.bg }}>
                              {agent.emoji}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="text-[11px] font-bold" style={{ color: agent.color }}>
                                  {agent.name}
                                </span>
                                <span className={'text-[9px] ' + (isDark ? 'text-gray-600' : 'text-gray-400')}>
                                  {formatTime(msg.createdAt || msg.time)}
                                </span>
                              </div>
                              <p className={'text-xs leading-relaxed break-words ' + (isDark ? 'text-gray-300' : 'text-gray-700')}>
                                {msg.text || '(빈 메시지)'}
                              </p>
                              {renderTgAttachment(msg)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

      </div>

      {/* Keyboard shortcut hint */}
      <div className={'fixed bottom-4 left-1/2 -translate-x-1/2 text-[10px] px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ' +
        (isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400')}
      >
        ⌘K 검색 · ⌘N 새 채팅 · Esc 닫기
      </div>

    </main>
  );
}
